import React, { useState, useEffect } from "react";
import "./UserDetails.css";
import { useNavigate, useParams } from "react-router-dom";
import { getUserById, updateUser, toggleUserStatus, verifyUser } from "../../services/userManagement";
import LoadingSpinner from "../common/LoadingSpinner/LoadingSpinner";

const UserDetails = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, [userId]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getUserById(userId);
      const userData = response.data || response;

      // Transform backend data to frontend expected format
      const transformedUserData = {
        _id: userData.id,
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        phoneNumber: userData.phoneNumber || 'N/A',
        verified: userData.verified,
        isBlocked: userData.isBlocked || false,
        role: userData.roles && userData.roles.length > 0 ? userData.roles[0] : 'BUYER',
        createdAt: userData.createdAt || new Date().toISOString(),
      };

      setUserData(transformedUserData);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setError("Failed to fetch user data. Please check your connection and try again.");
      setUserData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateUser(userId, userData);
      alert("User updated successfully!");
      navigate("/managements");
    } catch (error) {
      console.error("Error updating user:", error);
      setError("Failed to update user. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      await toggleUserStatus(userId, !userData.isBlocked);
      setUserData(prev => ({ ...prev, isBlocked: !prev.isBlocked }));
      alert(`User ${!userData.isBlocked ? 'blocked' : 'unblocked'} successfully!`);
    } catch (error) {
      console.error("Error toggling user status:", error);
      setError("Failed to update user status. Please try again.");
    }
  };

  const handleVerify = async () => {
    try {
      await verifyUser(userId);
      setUserData(prev => ({ ...prev, verified: true }));
      alert("User verified successfully!");
    } catch (error) {
      console.error("Error verifying user:", error);
      setError("Failed to verify user. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="user-details-container">
        <LoadingSpinner message="Loading user details..." />
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="user-details-container">
        <div className="error-container">
          <p>User not found</p>
          <button onClick={() => navigate("/managements")}>
            Back to Managements
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-details-container">
      <div className="user-details-header">
        <h1>User Information</h1>
        <div className="header-actions">
          <button
            className={`status-toggle-btn ${userData.isBlocked ? 'unblock' : 'block'}`}
            onClick={handleToggleStatus}
          >
            {userData.isBlocked ? 'Unblock User' : 'Block User'}
          </button>
          {!userData.verified && (
            <button className="verify-btn" onClick={handleVerify}>
              Verify User
            </button>
          )}
          <button className="save-button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <p style={{ fontSize: '0.9rem', color: '#6c757d', marginTop: '0.5rem' }}>
            This might be due to backend connectivity issues. Please ensure the backend server is running.
          </p>
          <button onClick={fetchUserData} className="retry-button">
            Retry
          </button>
        </div>
      )}

      <div className="details-section">
        <h2>Identity</h2>
        <div className="input-row">
          <div className="input-group">
            <label>First Name:</label>
            <div className="input-value">{userData.firstName}</div>
          </div>
          <div className="input-group">
            <label>Last Name:</label>
            <div className="input-value">{userData.lastName}</div>
          </div>
        </div>
      </div>

      <div className="details-section">
        <h2>Contact</h2>
        <div className="input-row">
          <div className="input-group">
            <label>Phone Number:</label>
            <div className="input-value">{userData.phoneNumber}</div>
          </div>
          <div className="input-group">
            <label>Email:</label>
            <div className="input-value">{userData.email}</div>
          </div>
        </div>
      </div>

      <div className="details-section">
        <h2>Account Information</h2>
        <div className="input-row">
          <div className="input-group">
            <label>Role:</label>
            <div className="input-value">{userData.role || 'User'}</div>
          </div>
          <div className="input-group">
            <label>Status:</label>
            <div className={`input-value status-badge ${userData.isBlocked ? 'blocked' : 'active'}`}>
              {userData.isBlocked ? 'Blocked' : 'Active'}
            </div>
          </div>
        </div>
        <div className="input-row">
          <div className="input-group">
            <label>Verification:</label>
            <div className={`input-value ${userData.verified ? "verified" : "not-verified"}`}>
              {userData.verified ? "Verified" : "Not Verified"}
            </div>
          </div>
          <div className="input-group">
            <label>Created:</label>
            <div className="input-value">
              {new Date(userData.createdAt).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>

      <div className="details-section">
        <h2>Actions</h2>
        <div className="input-row">
          <div className="input-group">
            <button className="reset-password-btn">Reset Password</button>
          </div>
          <div className="input-group">
            <button className="delete-user-btn">Delete User</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
